import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../utils/firebase";
import {
  Clock,
  UserCheck,
  Users,
  TrendingUp,
  Briefcase,
  Pill,
  Info,
  Award,
  Home,
} from "lucide-react";
import moment from "moment";

// Economic impact tooltip component
const EconomicImpactTooltip = () => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsTooltipVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={tooltipRef}>
      <Info
        className="text-slate-400 hover:text-slate-300 cursor-pointer"
        size={16}
        onClick={() => setIsTooltipVisible(!isTooltipVisible)}
      />
      {isTooltipVisible && (
        <div
          className="fixed right-4 top-22 w-80 p-3 bg-slate-800 text-white text-sm rounded-lg shadow-lg border border-slate-600 z-[9999]"
          style={{
            whiteSpace: "normal",
            overflowWrap: "break-word",
            maxWidth: "460px",
            minWidth: "460px",
          }}
        >
          <p className="mb-3">
            <strong className="text-cyan-400">Data Source (Apr 2025):</strong>{" "}
            All figures are in 2025 dollars, CPI-adjusted. We combine the latest
            U.S. cost estimates for alcohol misuse (<strong>$354B</strong>),
            non-opioid illicit drugs (<strong>$290B</strong>), and opioid use
            disorder/overdose (<strong>$1.22T</strong>) for a total annual
            burden of <strong>$1.864 trillion</strong>.
          </p>

          <p className="mb-3">
            <strong className="text-emerald-400">Calculation Method:</strong> We
            divide this total by approximately <strong>50 million</strong>{" "}
            Americans with any SUD (NSDUH 2022) to get{" "}
            <strong>$37,280 per person annually</strong>, or{" "}
            <strong>$102.14 per day</strong>.
          </p>

          <p>
            <strong className="text-amber-400">Applied Impact:</strong> For each
            resident, we multiply their days in the house (from intake to
            discharge for alumni, or intake to present for current residents) by
            the daily rate to calculate their total economic impact
            contribution.
          </p>
        </div>
      )}
    </div>
  );
};

const calculateTotalRecoveryTime = (allResidents) => {
  // Only count current residents from the residents collection (not alumni)
  const currentResidents = allResidents.filter(
    (resident) => !resident.isAlumni && resident.currentResident
  );

  // Group residents by house to avoid double-counting
  const residentsByHouse = {};

  currentResidents.forEach((resident) => {
    if (!resident.house) return;

    if (!residentsByHouse[resident.house]) {
      residentsByHouse[resident.house] = [];
    }

    residentsByHouse[resident.house].push(resident);
  });

  // Calculate sobriety per house
  const houseSobrietyTimes = {};

  Object.keys(residentsByHouse).forEach((house) => {
    const houseResidents = residentsByHouse[house];

    const houseSobriety = houseResidents.reduce(
      (total, resident) => {
        if (!resident.sobrietyDate) return total;

        // Calculate individual sobriety time
        const sobrietyDate = moment(resident.sobrietyDate, "YYYY-MM-DD");
        const now = moment();

        if (sobrietyDate.isValid()) {
          const duration = moment.duration(now.diff(sobrietyDate));

          const individualSobriety = {
            years: duration.years(),
            months: duration.months(),
            days: duration.days(),
          };

          // Add to house total using moment duration
          const duration1 = moment.duration({
            years: total.years || 0,
            months: total.months || 0,
            days: total.days || 0,
          });

          const duration2 = moment.duration({
            years: individualSobriety.years || 0,
            months: individualSobriety.months || 0,
            days: individualSobriety.days || 0,
          });

          const totalDuration = duration1.add(duration2);

          return {
            years: totalDuration.years(),
            months: totalDuration.months(),
            days: totalDuration.days(),
          };
        }

        return total;
      },
      { years: 0, months: 0, days: 0 }
    );

    houseSobrietyTimes[house] = houseSobriety;
  });

  // Sum up the house totals
  const totalSobriety = Object.values(houseSobrietyTimes).reduce(
    (total, houseSobriety) => {
      const duration1 = moment.duration({
        years: total.years || 0,
        months: total.months || 0,
        days: total.days || 0,
      });

      const duration2 = moment.duration({
        years: houseSobriety.years || 0,
        months: houseSobriety.months || 0,
        days: houseSobriety.days || 0,
      });

      const totalDuration = duration1.add(duration2);

      return {
        years: totalDuration.years(),
        months: totalDuration.months(),
        days: totalDuration.days(),
      };
    },
    { years: 0, months: 0, days: 0 }
  );

  // Calculate total days for easy average calculations
  const totalDays =
    totalSobriety.years * 365 + totalSobriety.months * 30 + totalSobriety.days;

  return {
    ...totalSobriety,
    totalDays,
    residentCount: currentResidents.length,
  };
};

// This component is specifically for the "Quick Glance" tab
const RCQuickGlance = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickGlanceData, setQuickGlanceData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!organizationId) {
          throw new Error("Organization ID is required");
        }

        setLoading(true);

        // Fetch all data concurrently for efficiency
        const alumniCollection = collection(
          db,
          `organizations/${organizationId}/alumni`
        );
        const residentsCollection = collection(
          db,
          `organizations/${organizationId}/residents`
        );
        const assessmentsCollection = collection(
          db,
          `organizations/${organizationId}/recoveryCapital`
        );

        const [alumniSnapshot, residentsSnapshot, assessmentsSnapshot] =
          await Promise.all([
            getDocs(alumniCollection),
            getDocs(residentsCollection),
            getDocs(assessmentsCollection),
          ]);

        if (alumniSnapshot.empty && residentsSnapshot.empty) {
          throw new Error("No data found");
        }

        // Process all datasets
        const alumniData = alumniSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          isAlumni: true,
        }));
        const residentsData = residentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          isAlumni: false,
        }));
        const assessmentsData = assessmentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Combine datasets
        const allResidents = [...alumniData, ...residentsData];

        // Calculate unique individuals
        const totalUniqueIndividuals = allResidents.length;

        // Calculate sobriety rate for ALUMNI ONLY
        const alumniSoberCount = alumniData.filter(
          (alumnus) => alumnus.sobrietyStatus === "Sober"
        ).length;

        const sobrietyRate =
          alumniData.length > 0
            ? (alumniSoberCount / alumniData.length) * 100
            : 0;

        // Process employment data function
        const processEmploymentData = (residentsArray, viewType) => {
          const fieldToCheck =
            viewType === "intake" ? "workOnEntry" : "workStatus";
          const employed = [];
          const unemployed = [];

          residentsArray.forEach((resident) => {
            const workStatus = resident[fieldToCheck];

            // Skip retired residents
            if (workStatus === "Retired") return;

            // Count as employed if Full-Time or Part-Time
            if (workStatus === "Full-Time" || workStatus === "Part-Time") {
              employed.push(resident);
            }
            // Count as unemployed if explicitly "Unemployed"
            else if (workStatus === "Unemployed") {
              unemployed.push(resident);
            }
            // Other values are not counted
          });

          return {
            employed: employed.length,
            unemployed: unemployed.length,
            total: employed.length + unemployed.length,
          };
        };

        // Calculate risk factor percentages from assessments
        // Replace your existing calculateRiskFactorPercentages function with this improved version:

        const calculateRiskFactorPercentages = (assessments) => {
          if (assessments.length === 0) return {};

          // Group assessments by residentName and get the most recent for each person
          const getLatestAssessmentPerResident = (assessments) => {
            const assessmentsByResident = {};

            assessments.forEach((assessment) => {
              const residentName = assessment.residentName;
              if (!residentName) return; // Skip if no resident name

              if (!assessmentsByResident[residentName]) {
                assessmentsByResident[residentName] = assessment;
              } else {
                // Compare timestamps to keep the most recent
                const currentTimestamp = assessment.timestamp;
                const existingTimestamp =
                  assessmentsByResident[residentName].timestamp;

                if (currentTimestamp > existingTimestamp) {
                  assessmentsByResident[residentName] = assessment;
                }
              }
            });

            return Object.values(assessmentsByResident);
          };

          // Get unique, most recent assessments
          const uniqueAssessments = getLatestAssessmentPerResident(assessments);

          const calculatePercentage = (fieldName, targetValue = "Yes") => {
            const residentsWithData = [];

            uniqueAssessments.forEach((assessment) => {
              let fieldValue = assessment[fieldName];

              // If the field is missing or null, try to find it in previous assessments for this resident
              if (fieldValue === undefined || fieldValue === null) {
                const residentName = assessment.residentName;
                const residentAssessments = assessments
                  .filter((a) => a.residentName === residentName)
                  .sort((a, b) => b.timestamp - a.timestamp); // Most recent first

                // Look through previous assessments for this field
                for (const prevAssessment of residentAssessments) {
                  if (
                    prevAssessment[fieldName] !== undefined &&
                    prevAssessment[fieldName] !== null
                  ) {
                    fieldValue = prevAssessment[fieldName];

                    break;
                  }
                }
              }

              // Only include in calculation if we found a value
              if (fieldValue !== undefined && fieldValue !== null) {
                residentsWithData.push({
                  residentName: assessment.residentName,
                  value: fieldValue,
                });
              }
            });

            if (residentsWithData.length === 0) return 0;

            const matchingCount = residentsWithData.filter(
              (resident) => resident.value === targetValue
            ).length;

            return parseFloat(
              ((matchingCount / residentsWithData.length) * 100).toFixed(1)
            );
          };

          return {
            childhoodTraumaRate: calculatePercentage("childhoodTrauma"),
            singleParentRate: calculatePercentage("twoParentsHome", "No"), // Note: "No" for single parent
            prisonRate: calculatePercentage("overOneYearInPrison"),
            hasChildrenRate: calculatePercentage("kids"),
            homelessRate: calculatePercentage("homelessOneYear"),
            coOccurringRate: calculatePercentage("coOccurringDiagnosis"),
            assessmentCount: uniqueAssessments.length, // Number of unique people who completed assessments
          };
        };

        const riskFactors = calculateRiskFactorPercentages(assessmentsData);

        // Calculate employment rates
        const intakeData = processEmploymentData(alumniData, "intake");
        const employmentRateAtIntake =
          intakeData.total > 0
            ? (intakeData.employed / intakeData.total) * 100
            : 0;

        const dischargeData = processEmploymentData(alumniData, "discharge");
        const employmentRateAtDischarge =
          dischargeData.total > 0
            ? (dischargeData.employed / dischargeData.total) * 100
            : 0;

        // Change this part in your calculateEconomicImpact function
        const calculateEconomicImpact = () => {
          let totalEconomicImpact = 0;

          // Calculate the daily impact per person
          const totalAnnualImpact =
            354_000_000_000 + 290_000_000_000 + 1_220_000_000_000;
          const annualImpactPerPerson = totalAnnualImpact / 50_000_000;
          const dailyImpact = annualImpactPerPerson / 365;

          // Function to calculate impact for a single resident
          const calculateResidentImpact = (resident) => {
            if (!resident.intakeDate) return 0;

            const startDate = moment(resident.intakeDate, "YYYY-MM-DD");
            const endDate =
              resident.dischargeDate && resident.isAlumni
                ? moment(resident.dischargeDate, "YYYY-MM-DD")
                : moment(); // Use current date for active residents

            const totalDays = endDate.diff(startDate, "days");
            return Math.max(0, totalDays * dailyImpact);
          };

          // CHANGE: Calculate for ALL alumni, not just sober ones
          alumniData.forEach((alumnus) => {
            totalEconomicImpact += calculateResidentImpact(alumnus);
          });

          // CHANGE: Calculate for ALL current residents, not just sober ones
          residentsData.forEach((resident) => {
            totalEconomicImpact += calculateResidentImpact(resident);
          });

          console.log("Economic impact calculation:", {
            alumniCount: alumniData.length,
            residentsCount: residentsData.length,
            totalEconomicImpact,
          });

          return parseFloat(totalEconomicImpact.toFixed(2));
        };

        const economicImpact = calculateEconomicImpact();

        // MAT usage rate from ALL residents (for prevalence)
        const matUsers = allResidents.filter((resident) => resident.isMAT);
        const matRate =
          allResidents.length > 0
            ? (matUsers.length / allResidents.length) * 100
            : 0;

        // Sobriety rates from ALUMNI ONLY
        // Replace the MAT calculation with this:
        const matAlumni = alumniData.filter((alumni) => alumni.isMAT === true);
        const nonMatAlumni = alumniData.filter((alumni) => !alumni.isMAT);

        const matSoberUsers = matAlumni.filter(
          (alumni) => alumni.sobrietyStatus === "Sober"
        );
        const matSobrietyRate =
          matAlumni.length > 0
            ? (matSoberUsers.length / matAlumni.length) * 100
            : 0;

        const nonMatSoberUsers = nonMatAlumni.filter(
          (alumni) => alumni.sobrietyStatus === "Sober"
        );
        const nonMatSobrietyRate =
          nonMatAlumni.length > 0
            ? (nonMatSoberUsers.length / nonMatAlumni.length) * 100
            : 0;

        // Calculate time-based recovery metrics with more granular intervals
        const calculateTimeBased = (alumniData) => {
          // Track counts for each time period
          const counts = {
            threeMonth: { total: 0, sober: 0, residents: [] },
            sixMonth: { total: 0, sober: 0, residents: [] },
            nineMonth: { total: 0, sober: 0, residents: [] },
            oneYear: { total: 0, sober: 0, residents: [] },
            twoYear: { total: 0, sober: 0, residents: [] },
            threeYear: { total: 0, sober: 0, residents: [] },
          };

          // ONLY process alumni data
          alumniData.forEach((alumnus) => {
            if (!alumnus.sobrietyDate || !alumnus.dischargeDate) return;

            // Calculate days sober AT DISCHARGE (not current date)
            const sobrietyDate = moment(alumnus.sobrietyDate);
            const dischargeDate = moment(alumnus.dischargeDate);
            const daysSober = dischargeDate.diff(sobrietyDate, "days");

            // Skip if negative (data quality issue)
            if (daysSober < 0) return;

            // Create resident summary for debugging
            const residentSummary = {
              name: `${alumnus.firstName} ${alumnus.lastName}`,
              daysSober: daysSober,
              sobrietyDate: alumnus.sobrietyDate,
              dischargeDate: alumnus.dischargeDate,
              sobrietyStatus: alumnus.sobrietyStatus,
              lengthOfStay: daysSober, // How long they stayed
            };

            // Determine if resident was sober AT DISCHARGE
            const wasSoberAtDischarge = alumnus.sobrietyStatus === "Sober";

            // Categorize by LENGTH OF STAY (not current sobriety time)
            if (daysSober <= 90) {
              counts.threeMonth.total++;
              counts.threeMonth.residents.push(residentSummary);
              if (wasSoberAtDischarge) counts.threeMonth.sober++;
            } else if (daysSober <= 180) {
              counts.sixMonth.total++;
              counts.sixMonth.residents.push(residentSummary);
              if (wasSoberAtDischarge) counts.sixMonth.sober++;
            } else if (daysSober <= 270) {
              counts.nineMonth.total++;
              counts.nineMonth.residents.push(residentSummary);
              if (wasSoberAtDischarge) counts.nineMonth.sober++;
            } else if (daysSober <= 365) {
              counts.oneYear.total++;
              counts.oneYear.residents.push(residentSummary);
              if (wasSoberAtDischarge) counts.oneYear.sober++;
            } else if (daysSober <= 730) {
              counts.twoYear.total++;
              counts.twoYear.residents.push(residentSummary);
              if (wasSoberAtDischarge) counts.twoYear.sober++;
            } else {
              counts.threeYear.total++;
              counts.threeYear.residents.push(residentSummary);
              if (wasSoberAtDischarge) counts.threeYear.sober++;
            }
          });

          // Calculate percentages
          const calculatePercentage = (period) => {
            return counts[period].total > 0
              ? parseFloat(
                  ((counts[period].sober / counts[period].total) * 100).toFixed(
                    1
                  )
                )
              : 0;
          };

          return {
            threeMonth: calculatePercentage("threeMonth"),
            sixMonth: calculatePercentage("sixMonth"),
            nineMonth: calculatePercentage("nineMonth"),
            oneYear: calculatePercentage("oneYear"),
            twoYear: calculatePercentage("twoYear"),
            threeYear: calculatePercentage("threeYear"),
            // Add count values
            threeMonthCount: counts.threeMonth.total,
            sixMonthCount: counts.sixMonth.total,
            nineMonthCount: counts.nineMonth.total,
            oneYearCount: counts.oneYear.total,
            twoYearCount: counts.twoYear.total,
            threeYearCount: counts.threeYear.total,
          };
        };

        // Call the function with allResidents data
        const timeBasedRecovery = calculateTimeBased(alumniData);
        const totalRecoveryTime = calculateTotalRecoveryTime(allResidents);

        // Set the aggregated data
        // Calculate probation/parole data
        const probationParoleUsers = allResidents.filter(
          (resident) => resident.poFirstName || resident.poLastName
        );
        const probationParoleRate =
          allResidents.length > 0
            ? (probationParoleUsers.length / allResidents.length) * 100
            : 0;

        // Calculate age demographics
        const ageRanges = {
          "18-25": { count: 0 },
          "26-35": { count: 0 },
          "36-45": { count: 0 },
          "46-55": { count: 0 },
          "56-65+": { count: 0 },
        };

        allResidents.forEach((resident) => {
          if (resident.dob) {
            const age = moment().diff(moment(resident.dob), "years");
            const ageRange =
              age <= 25
                ? "18-25"
                : age <= 35
                ? "26-35"
                : age <= 45
                ? "36-45"
                : age <= 55
                ? "46-55"
                : "56-65+";
            ageRanges[ageRange].count++;
          }
        });

        // Find most common age range
        let mostCommonAgeRange = Object.keys(ageRanges)[0];
        let maxCount = 0;

        Object.entries(ageRanges).forEach(([range, data]) => {
          if (data.count > maxCount) {
            maxCount = data.count;
            mostCommonAgeRange = range;
          }
        });

        // Then add these to your quickGlanceData state
        setQuickGlanceData({
          totalUniqueIndividuals,
          sobrietyRate: parseFloat(sobrietyRate.toFixed(1)),
          employmentRateAtIntake: parseFloat(employmentRateAtIntake.toFixed(1)),
          employmentRateAtDischarge: parseFloat(
            employmentRateAtDischarge.toFixed(1)
          ),
          economicImpact,
          matRate: parseFloat(matRate.toFixed(1)),
          matSobrietyRate: parseFloat(matSobrietyRate.toFixed(1)),
          nonMatSobrietyRate: parseFloat(nonMatSobrietyRate.toFixed(1)),
          timeBasedRecovery: timeBasedRecovery,
          totalRecoveryTime: totalRecoveryTime,
          probationParoleRate: parseFloat(probationParoleRate.toFixed(1)),
          mostCommonAgeRange: mostCommonAgeRange,
          ageRanges: ageRanges,
          ...riskFactors, // This spreads all the risk factor data including assessmentCount
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  if (!quickGlanceData) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <p className="text-white">No recovery data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Headline */}
      <div className="mb-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
            Recovery in Numbers: The Impact of Reverence House
          </span>
        </h2>
        <p className="text-slate-400 text-lg md:text-xl max-w-3xl mx-auto">
          A data-driven look at our outcomes, success stories, and the lives
          transformed through our recovery programs.
        </p>
      </div>

      {/* "At a Glance" Section */}
      <div className="mb-12">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-10 w-1 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full" />
          <h2 className="text-2xl font-bold text-white">At a Glance</h2>
        </div>

        <div className="space-y-6">
          {/* Resident Profile & Risk Factors - Move to top */}
          <div className="group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-blue-500/30">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Users className="text-blue-400" size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Resident Profile & Risk Factors
                  </h3>
                  <p className="text-sm text-slate-400">
                    Based on {quickGlanceData.assessmentCount || 0} residents
                    with completed questionnaires
                  </p>
                </div>
              </div>
              <span className="relative inline-block group">
                <Info size={16} className="text-slate-400 cursor-pointer" />
                <span className="pointer-events-none absolute right-0 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
                  <div className="space-y-2">
                    <div>
                      <strong className="text-blue-400">Data Source:</strong>{" "}
                      Risk factor percentages are calculated from Recovery
                      Capital Questionnaires completed by residents.
                    </div>
                    <div>
                      <strong className="text-amber-400">Sample Size:</strong>{" "}
                      Based on {quickGlanceData.assessmentCount || 0} residents
                      with completed assessments.
                    </div>
                    <div>
                      <strong className="text-red-400">Important:</strong> Only
                      residents who have completed at least one Recovery Capital
                      Questionnaire are included. Organizations with existing
                      residents prior to system implementation may not have
                      complete data coverage.
                    </div>
                  </div>
                </span>
              </span>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Demographics */}
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {quickGlanceData.mostCommonAgeRange}
                  </div>
                  <p className="text-xs text-slate-400">Most common age</p>
                </div>
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-400">
                    {quickGlanceData.probationParoleRate}%
                  </div>
                  <p className="text-xs text-slate-400">On probation/parole</p>
                </div>

                {/* Risk Factors */}
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {quickGlanceData.childhoodTraumaRate || 0}%
                  </div>
                  <p className="text-xs text-slate-400">Childhood trauma</p>
                </div>
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-400">
                    {quickGlanceData.singleParentRate || 0}%
                  </div>
                  <p className="text-xs text-slate-400">Single parent home</p>
                </div>
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    {quickGlanceData.prisonRate || 0}%
                  </div>
                  <p className="text-xs text-slate-400">1+ year in prison</p>
                </div>
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {quickGlanceData.hasChildrenRate || 0}%
                  </div>
                  <p className="text-xs text-slate-400">Have children</p>
                </div>
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-indigo-400">
                    {quickGlanceData.homelessRate || 0}%
                  </div>
                  <p className="text-xs text-slate-400">1+ year homeless</p>
                </div>
                <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-pink-400">
                    {quickGlanceData.coOccurringRate || 0}%
                  </div>
                  <p className="text-xs text-slate-400">
                    Co-occurring disorder
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Other metrics - Properly aligned grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Total Residents */}
            <div className="group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-cyan-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg">
                    <Users className="text-cyan-400" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Total Residents
                  </h3>
                </div>
              </div>
              <div className="p-6 flex flex-col items-center text-center">
                <div className="text-5xl font-bold text-cyan-400 mb-3">
                  {quickGlanceData.totalUniqueIndividuals}
                </div>
                <p className="text-slate-300">
                  Unique residents tracked through our recovery portal
                </p>
              </div>
            </div>

            {/* Sobriety Rate */}
            <div className="group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-emerald-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg">
                    <UserCheck className="text-emerald-400" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Sobriety Success
                  </h3>
                </div>
              </div>
              <div className="p-6 flex flex-col items-center text-center">
                <div className="text-5xl font-bold text-emerald-400 mb-3">
                  {quickGlanceData.sobrietyRate}%
                </div>
                <p className="text-slate-300">
                  Residents leave Reverence House sober and on a path to
                  recovery
                </p>
              </div>
            </div>

            {/* Employment Transformation */}
            <div className="group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-purple-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Briefcase className="text-purple-400" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Employment Transformation
                  </h3>
                </div>
              </div>
              <div className="p-6 flex flex-col items-center text-center">
                <div className="flex items-end gap-4 mb-3">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-slate-400">
                      {quickGlanceData.employmentRateAtIntake.toFixed(1)}%
                    </div>
                    <div className="text-sm text-slate-500">At Intake</div>
                  </div>
                  <div className="text-4xl text-slate-500">→</div>
                  <div className="text-center">
                    <div className="text-5xl font-bold text-purple-400">
                      {quickGlanceData.employmentRateAtDischarge.toFixed(1)}%
                    </div>
                    <div className="text-sm text-slate-500">At Discharge</div>
                  </div>
                </div>
                <p className="text-slate-300">
                  Employment rate increases dramatically during recovery
                </p>
              </div>
            </div>

            {/* Economic Impact */}
            <div className="group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-green-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <TrendingUp className="text-green-400" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Economic Impact
                  </h3>
                </div>
                <EconomicImpactTooltip />
              </div>
              <div className="p-6 flex flex-col items-center text-center">
                <div className="text-5xl font-bold text-green-400 mb-3">
                  ${quickGlanceData.economicImpact.toLocaleString()}
                </div>
                <p className="text-slate-300">
                  Savings to society based on reduced economic burden of
                  substance use disorders
                </p>
              </div>
            </div>

            {/* MAT Statistics */}
            <div className="md:col-span-2 group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-pink-500/30">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-500/10 rounded-lg">
                    <Pill className="text-pink-400" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Medication-Assisted Treatment
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-3 gap-6 items-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-pink-400 mb-2">
                      {quickGlanceData.matRate.toFixed(1)}%
                    </div>
                    <p className="text-slate-300">Residents utilizing MAT</p>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-pink-400 mb-1">
                      {quickGlanceData.matSobrietyRate.toFixed(1)}%
                    </div>
                    <p className="text-sm text-slate-400">
                      Sobriety rate with MAT
                    </p>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-cyan-400 mb-1">
                      {quickGlanceData.nonMatSobrietyRate.toFixed(1)}%
                    </div>
                    <p className="text-sm text-slate-400">
                      Sobriety rate without MAT
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-12">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-10 w-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" />
          <h2 className="text-2xl font-bold text-white">Time-Based Recovery</h2>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Clock className="text-blue-400" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Recovery Success Over Time
              </h3>
            </div>
            <span className="relative inline-block group">
              <Info size={16} className="text-slate-400 cursor-pointer" />
              <span className="pointer-events-none absolute right-0 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
                <div className="space-y-2">
                  <div>
                    <strong className="text-blue-400">What this shows:</strong>{" "}
                    Success rates by length of sobriety achieved before leaving
                    our program. Only includes alumni who completed the program.
                  </div>
                  <div>
                    <strong className="text-green-400">
                      Questions it answers:
                    </strong>{" "}
                    "Do people with longer sobriety have better outcomes?" and
                    "When people leave your program in the specific time buckets
                    listed, what % of them are sober?"
                  </div>
                  <div>
                    <strong className="text-amber-400">
                      How it's calculated:
                    </strong>{" "}
                    We group alumni by their sobriety length (sobriety date to
                    discharge date) and show what percentage in each group left
                    sober.
                  </div>
                  <div>
                    <strong className="text-red-400">Important:</strong> Current
                    residents are not included until they complete the program.
                  </div>
                </div>
              </span>
            </span>
          </div>
          <div className="space-y-4">
            {/* 3 Months */}
            <div className="flex justify-between items-center">
              <span className="text-slate-400">0-3 Months</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm">
                  ({quickGlanceData.timeBasedRecovery.threeMonthCount || 0})
                </span>
                <span className="text-white font-bold">
                  {quickGlanceData.timeBasedRecovery.threeMonth}%
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-700/30 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${quickGlanceData.timeBasedRecovery.threeMonth}%`,
                }}
              ></div>
            </div>

            {/* 6 Months */}
            <div className="flex justify-between items-center">
              <span className="text-slate-400">3-6 Months</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm">
                  ({quickGlanceData.timeBasedRecovery.sixMonthCount || 0})
                </span>
                <span className="text-white font-bold">
                  {quickGlanceData.timeBasedRecovery.sixMonth}%
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-700/30 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${quickGlanceData.timeBasedRecovery.sixMonth}%`,
                }}
              ></div>
            </div>

            {/* 9 Months */}
            <div className="flex justify-between items-center">
              <span className="text-slate-400">6-9 Months</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm">
                  ({quickGlanceData.timeBasedRecovery.nineMonthCount || 0})
                </span>
                <span className="text-white font-bold">
                  {quickGlanceData.timeBasedRecovery.nineMonth}%
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-700/30 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${quickGlanceData.timeBasedRecovery.nineMonth}%`,
                }}
              ></div>
            </div>

            {/* 1 Year */}
            <div className="flex justify-between items-center">
              <span className="text-slate-400">9 Months-1 Year</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm">
                  ({quickGlanceData.timeBasedRecovery.oneYearCount || 0})
                </span>
                <span className="text-white font-bold">
                  {quickGlanceData.timeBasedRecovery.oneYear}%
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-700/30 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${quickGlanceData.timeBasedRecovery.oneYear}%`,
                }}
              ></div>
            </div>

            {/* 2 Years */}
            <div className="flex justify-between items-center">
              <span className="text-slate-400">1-2 Years</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm">
                  ({quickGlanceData.timeBasedRecovery.twoYearCount || 0})
                </span>
                <span className="text-white font-bold">
                  {quickGlanceData.timeBasedRecovery.twoYear}%
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-700/30 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${quickGlanceData.timeBasedRecovery.twoYear}%`,
                }}
              ></div>
            </div>

            {/* 3+ Years */}
            <div className="flex justify-between items-center">
              <span className="text-slate-400">2-3+ Years</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm">
                  ({quickGlanceData.timeBasedRecovery.threeYearCount || 0})
                </span>
                <span className="text-white font-bold">
                  {quickGlanceData.timeBasedRecovery.threeYear}%
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-700/30 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{
                  width: `${quickGlanceData.timeBasedRecovery.threeYear}%`,
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Milestone Achievements */}
      <div className="mb-12">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-10 w-1 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
          <h2 className="text-2xl font-bold text-white">
            Milestone Achievements
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Recovery Time */}
          <div className="group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-cyan-500/30">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Clock className="text-cyan-400" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  Total Recovery Time
                </h3>
              </div>
            </div>
            <div className="p-6 flex flex-col items-center text-center">
              <div className="text-5xl font-bold text-cyan-400 mb-2">
                {quickGlanceData.totalRecoveryTime
                  ? quickGlanceData.totalRecoveryTime.years
                  : 0}
              </div>
              <p className="text-xl text-slate-300 mb-2">Years</p>

              {/* Add months and days */}
              <div className="text-md text-cyan-400/80 mb-4">
                +
                {quickGlanceData.totalRecoveryTime
                  ? quickGlanceData.totalRecoveryTime.months
                  : 0}
                m{" "}
                {quickGlanceData.totalRecoveryTime
                  ? quickGlanceData.totalRecoveryTime.days
                  : 0}
                d
              </div>

              <p className="text-slate-400 text-sm">
                Cumulative sobriety across{" "}
                {quickGlanceData.totalRecoveryTime?.residentCount || 0}{" "}
                residents
              </p>

              {/* Add average per resident */}
              <div className="mt-2 pt-2 border-t border-slate-700/30 w-full">
                <p className="text-cyan-300 text-sm font-medium">
                  Average Per Resident
                </p>
                <p className="text-white text-lg">
                  {quickGlanceData.totalRecoveryTime &&
                  quickGlanceData.totalRecoveryTime.residentCount > 0
                    ? `${Math.floor(
                        quickGlanceData.totalRecoveryTime.totalDays /
                          365 /
                          quickGlanceData.totalRecoveryTime.residentCount
                      )}y ${Math.floor(
                        ((quickGlanceData.totalRecoveryTime.totalDays /
                          quickGlanceData.totalRecoveryTime.residentCount) %
                          365) /
                          30
                      )}m`
                    : "0y 0m"}
                </p>
              </div>
            </div>
          </div>

          {/* New Property Success */}
          <div className="md:col-span-2 group relative bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 border border-slate-700/50 hover:border-emerald-500/30">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <Award className="text-emerald-400" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  Reoccurrence-Free Milestones
                </h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-emerald-900/20 rounded-lg p-4 border border-emerald-800/30">
                  <h4 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                    <Home size={18} /> Level 1 & 2 Property
                  </h4>
                  <div className="text-4xl font-bold text-white mb-2">
                    1 Year
                  </div>
                  <p className="text-slate-300">
                    Our new property housing Level 1 and 2 recovery has
                    maintained
                    <span className="text-emerald-400 font-bold">
                      {" "}
                      zero reoccurrences
                    </span>{" "}
                    since opening over one year ago!
                  </p>
                </div>
                <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-800/30">
                  <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                    <Home size={18} /> Webster Property (Level 3)
                  </h4>
                  <div className="text-4xl font-bold text-white mb-2">
                    8 Months, 5 Days
                  </div>
                  <p className="text-slate-300">
                    Current record for consecutive days without reoccurrence at
                    our Level 3 transitional housing
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCQuickGlance;
